import { create } from 'zustand';
import { Platform } from 'react-native';
import * as SecureStore from 'expo-secure-store';
import { User } from '../shared/types/auth';
import { authApi } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';

interface AuthState {
  user: User | null;
  token: string | null;
  initialized: boolean; // True once initialize() has completed (success or failure)
  initializing: boolean; // True while initialize() is running
  setAuth: (user: User, token: string) => void;
  clearAuth: () => void;
  isAuthenticated: () => boolean;
  initialize: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

// Secure storage utility - uses SecureStore on mobile, localStorage on web (with minimal data)
const STORAGE_KEYS = {
  TOKEN: 'auth_token',
  USER_ID: 'auth_user_id', // Only store minimal user data
  USER_EMAIL: 'auth_user_email',
  USER_ROLE: 'auth_user_role',
  USER_DISPLAY_NAME: 'auth_user_display_name',
};

async function getSecureItem(key: string): Promise<string | null> {
  try {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.localStorage) {
        return localStorage.getItem(key);
      }
      return null;
    } else {
      return await SecureStore.getItemAsync(key);
    }
  } catch (error) {
    return null;
  }
}

async function setSecureItem(key: string, value: string): Promise<void> {
  try {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(key, value);
      }
    } else {
      await SecureStore.setItemAsync(key, value);
    }
  } catch (error) {
    // Silently fail - storage might not be available
  }
}

async function removeSecureItem(key: string): Promise<void> {
  try {
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(key);
      }
    } else {
      await SecureStore.deleteItemAsync(key);
    }
  } catch (error) {
    // Silently fail
  }
}

// Initialize from secure storage
const initializeAuth = async (): Promise<{ user: User | null; token: string | null }> => {
  try {
    // On web, token is in httpOnly cookie (not accessible to JS), so we use a placeholder
    // On mobile, get token from SecureStore
    let token: string | null = null;
    if (Platform.OS !== 'web') {
      token = await getSecureItem(STORAGE_KEYS.TOKEN);
    } else {
      // Web: Token is in httpOnly cookie, use placeholder to indicate auth exists
      // The actual token will be sent automatically with requests via cookies
      const userId = await getSecureItem(STORAGE_KEYS.USER_ID);
      if (userId) {
        token = 'cookie'; // Placeholder to indicate auth exists (cookie handles actual token)
      }
    }
    
    if (!token) {
      return { user: null, token: null };
    }

    // Only restore minimal user data from storage
    const userId = await getSecureItem(STORAGE_KEYS.USER_ID);
    const userEmail = await getSecureItem(STORAGE_KEYS.USER_EMAIL);
    const userRole = await getSecureItem(STORAGE_KEYS.USER_ROLE);
    const userDisplayName = await getSecureItem(STORAGE_KEYS.USER_DISPLAY_NAME);

    if (userId && userEmail) {
      const user: User = {
        id: parseInt(userId, 10),
        email: userEmail,
        display_name: userDisplayName || undefined,
        role: (userRole as 'admin' | 'user') || 'user',
      };
      return { user, token };
    }
  } catch (error) {
    // Clear corrupted data
    await clearSecureAuth();
  }

  return { user: null, token: null };
};

async function clearSecureAuth(): Promise<void> {
  await Promise.all([
    removeSecureItem(STORAGE_KEYS.TOKEN),
    removeSecureItem(STORAGE_KEYS.USER_ID),
    removeSecureItem(STORAGE_KEYS.USER_EMAIL),
    removeSecureItem(STORAGE_KEYS.USER_ROLE),
    removeSecureItem(STORAGE_KEYS.USER_DISPLAY_NAME),
  ]);
}

async function saveSecureAuth(user: User, token: string): Promise<void> {
  // On web, token is stored in httpOnly cookie by server, so we don't store it in localStorage
  // On mobile, we store token in SecureStore
  if (Platform.OS === 'web') {
    // Web: Only store minimal user data, token is in httpOnly cookie
    await Promise.all([
      setSecureItem(STORAGE_KEYS.USER_ID, user.id.toString()),
      setSecureItem(STORAGE_KEYS.USER_EMAIL, user.email),
      setSecureItem(STORAGE_KEYS.USER_ROLE, user.role || 'user'),
      setSecureItem(STORAGE_KEYS.USER_DISPLAY_NAME, user.display_name || ''),
    ]);
    // Remove token from localStorage if it exists (migration cleanup)
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem('auth_token');
    }
  } else {
    // Mobile: Store token in SecureStore
    await Promise.all([
      setSecureItem(STORAGE_KEYS.TOKEN, token),
      setSecureItem(STORAGE_KEYS.USER_ID, user.id.toString()),
      setSecureItem(STORAGE_KEYS.USER_EMAIL, user.email),
      setSecureItem(STORAGE_KEYS.USER_ROLE, user.role || 'user'),
      setSecureItem(STORAGE_KEYS.USER_DISPLAY_NAME, user.display_name || ''),
    ]);
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  initialized: false,
  initializing: false,
  setAuth: (user, token) => {
    // Update state immediately (synchronous)
    set({ user, token });
    // Store securely in background (asynchronous, fire and forget)
    saveSecureAuth(user, token).catch(() => {
      // Silently fail - storage errors shouldn't block auth
    });
  },
  clearAuth: async () => {
    set({ user: null, token: null });
    // Call logout endpoint to clear httpOnly cookie on web
    if (Platform.OS === 'web') {
      try {
        await authApi.logout();
      } catch (error) {
        // Silently fail - will clear local storage anyway
      }
    }
    await clearSecureAuth();
  },
  isAuthenticated: () => {
    return get().user !== null && get().token !== null;
  },
  initialize: async () => {
    // Prevent multiple simultaneous initializations
    if (get().initializing || get().initialized) {
      return;
    }
    
    set({ initializing: true });
    
    try {
      const { user, token } = await initializeAuth();
      set({ user, token });
      
      // Always verify user from server on initialization
      if (token) {
        try {
          const response = await authApi.getMe();
          
          if (response.user) {
            // Ensure role is always set
            const userWithRole = {
              ...response.user,
              role: response.user.role || 'user',
            };
            set({ user: userWithRole });
            // Update secure storage with fresh user data
            await saveSecureAuth(userWithRole, token);
          }
        } catch (error: any) {
          // If token is invalid or server unavailable, clear auth
          if (error && typeof error === 'object' && 'status' in error) {
            if (error.status === 401) {
              // Unauthorized - clear invalid token
              set({ user: null, token: null });
              await clearSecureAuth();
            } else if (error.status === 408 || error.message?.includes('timeout')) {
              // Timeout - server unavailable, but keep local auth state
              // User can still use app offline, will retry on next action
              logger.warn('Server unavailable during initialization, keeping local state', 'auth');
            }
          } else {
            // Network error or other - keep local state but log
            logger.warn('Error during initialization', 'auth', { message: error?.message || 'Unknown error' });
          }
        }
      }
    } catch (error) {
      // Critical error in initializeAuth - clear everything
      logger.error('Critical error during initialization', error, 'auth');
      set({ user: null, token: null });
      await clearSecureAuth();
    } finally {
      // Always mark as initialized, even on error
      set({ initialized: true, initializing: false });
    }
  },
  refreshUser: async () => {
    const { token } = get();
    if (!token) return;
    
    try {
      const response = await authApi.getMe();
      if (response.user) {
        // Ensure role is always set
        const userWithRole = {
          ...response.user,
          role: response.user.role || 'user',
        };
        set({ user: userWithRole });
        await saveSecureAuth(userWithRole, token);
      }
    } catch (error) {
      // Silently fail - will be handled on next initialize
    }
  },
}));

